import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/site/Reveal";
import { useSeo } from "../lib/seo";
import { CONTACT } from "../lib/site";
import { LEGAL } from "../constants/testIds";

export default function Terms() {
  useSeo({
    title: "Terms of Use | Alae Dental",
    description:
      "The terms that apply to your use of the Alae Dental website.",
    path: "/terms",
  });

  return (
    <main data-testid={LEGAL.termsPage} className="bg-linen text-umber">
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-paper paper-grain">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">Legal</div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.04] tracking-tight text-umber">
              Terms of Use
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 font-meta text-umber/70">
              Last updated: {new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 paper-grain">
        <div className="max-w-3xl mx-auto px-6 md:px-10 prose-alae">
          <Reveal>
            <div data-testid={LEGAL.notice} className="rounded-sm border border-forest/25 bg-paper/60 p-5 mb-10 text-sm text-umber/85 leading-relaxed">
              <strong>Placeholder notice.</strong> This document is a working
              draft intended as a starting point. It should be reviewed and
              adapted by legal counsel before the site launches publicly.
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">1. Acceptance</h2>
            <p>
              By using the Alae Dental website (the &ldquo;Site&rdquo;), you
              agree to these Terms of Use. If you do not agree, please do not
              use the Site.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">2. Not medical advice</h2>
            <p>
              The information on this Site is provided for general educational
              purposes only. It is not a substitute for professional dental or
              medical advice, diagnosis, or treatment. Always consult a
              qualified professional about any concerns regarding your oral or
              general health. Nothing on this Site creates a
              dentist-patient relationship until you become a patient of the
              practice.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">3. Appointments and booking</h2>
            <p>
              Booking requests submitted through this Site (whether through our
              online booking system or by callback request) are not confirmed
              until we contact you and finalize the details. Cancellation and
              rescheduling policies are shared at the time of booking and may
              be updated from time to time.
            </p>
          </Reveal>

          <Reveal delay={140}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">4. Use of the Site</h2>
            <p>You agree to use the Site only for lawful purposes and not to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-umber/85">
              <li>Submit false, misleading, or fraudulent information.</li>
              <li>Attempt to gain unauthorized access to any part of the Site.</li>
              <li>Interfere with the operation of the Site.</li>
              <li>Copy, reproduce, or redistribute content from the Site without our written permission.</li>
            </ul>
          </Reveal>

          <Reveal delay={160}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">5. Intellectual property</h2>
            <p>
              All text, images, graphics, logos, and other content on the Site
              are the property of Alae Dental or its licensors and are
              protected by copyright and other laws. You may not use them
              without our written permission, except that you may view them in
              a browser for your personal, non-commercial use.
            </p>
          </Reveal>

          <Reveal delay={180}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">6. Third-party links and services</h2>
            <p>
              The Site may link to third-party websites and services (for
              example, our online booking system, Google Maps, or our Google
              Business Profile). We are not responsible for the content,
              practices, or accuracy of any third-party site.
            </p>
          </Reveal>

          <Reveal delay={200}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">7. Disclaimer of warranties</h2>
            <p>
              The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as
              available&rdquo; basis. To the fullest extent permitted by law,
              we make no warranties, express or implied, regarding the Site,
              including its accuracy, availability, or fitness for a particular
              purpose.
            </p>
          </Reveal>

          <Reveal delay={220}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">8. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, Alae Dental and its
              team members will not be liable for any indirect, incidental,
              special, or consequential damages arising from your use of the
              Site.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">9. Governing law</h2>
            <p>
              These Terms are governed by the laws of the Province of Ontario
              and the federal laws of Canada that apply in Ontario, without
              regard to conflict-of-laws principles.
            </p>
          </Reveal>

          <Reveal delay={260}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">10. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will
              update the &ldquo;Last updated&rdquo; date above. Your continued
              use of the Site after any change means you accept the updated
              Terms.
            </p>
          </Reveal>

          <Reveal delay={280}>
            <h2 className="font-display text-3xl text-umber mt-10 mb-4">11. Contact</h2>
            <p>
              If you have questions about these Terms, please reach out:
            </p>
            <ul className="list-none pl-0 space-y-1 mb-6 text-umber/85">
              <li>By email: <a className="text-forest underline underline-offset-4" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></li>
              <li>By phone: <a className="text-forest underline underline-offset-4" href={`tel:${CONTACT.phoneTel}`}>{CONTACT.phoneLabel}</a></li>
              <li>By mail: Alae Dental, {CONTACT.address}</li>
            </ul>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-16 pt-8 border-t border-umber/20">
              <Link
                to="/"
                data-testid={LEGAL.returnHome}
                className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-forest"
              >
                Return home
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
